// Quote helpers: money formatting, date formatting, short-lead detection,
// blank-rate initialization, and the live preview text builder. Pure functions
// so they unit-test cleanly (Tier 2) and so the panel + the CF can share the
// exact same formatting (single source of truth for the guest email text).

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const DAY_MS = 86400000;

// Ordinal suffix for a day-of-month (1st, 2nd, 3rd, 4th, 11th, 21st, ...).
function ordinal(n) {
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return n + "st";
  if (j === 2 && k !== 12) return n + "nd";
  if (j === 3 && k !== 13) return n + "rd";
  return n + "th";
}

// Parse an ISO date string (YYYY-MM-DD) as UTC midnight. Returns null if invalid.
function parseISO(iso) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00Z");
  return Number.isNaN(d.getTime()) ? null : d;
}

// To ISO date string (YYYY-MM-DD) from a Date, using UTC parts.
function toISO(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Format a money amount: whole dollars without cents, cents only when present.
 * Thousands separators (en-US). 19500 -> "$19,500"; 2437.5 -> "$2,437.50".
 * @param {number} n
 * @returns {string}
 */
export function formatMoney(n) {
  if (n == null || Number.isNaN(n)) return "$0";
  const rounded = Math.round(n * 100) / 100;
  const hasCents = Math.round(rounded) !== rounded;
  const str = rounded.toLocaleString("en-US", {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return "$" + str;
}

/**
 * Format an ISO date as "June 28th" (full month name + ordinal day, no year).
 * @param {string} isoDate
 * @returns {string}
 */
export function formatOrdinalDate(isoDate) {
  const d = parseISO(isoDate);
  if (!d) return "";
  return `${MONTHS[d.getUTCMonth()]} ${ordinal(d.getUTCDate())}`;
}

/**
 * Format an ISO date as "June 28th, 2026" (ordinal day + year, comma-separated).
 * Used for installment due-date lines.
 * @param {string} isoDate
 * @returns {string}
 */
export function formatOrdinalDateYear(isoDate) {
  const d = parseISO(isoDate);
  if (!d) return "";
  return `${MONTHS[d.getUTCMonth()]} ${ordinal(d.getUTCDate())}, ${d.getUTCFullYear()}`;
}

/**
 * Format an ISO date as "June 28" (full month name + bare day number, no ordinal,
 * no year). Used for per-night rate lines in the differing-rates preview.
 * @param {string} isoDate
 * @returns {string}
 */
export function formatMonthDay(isoDate) {
  const d = parseISO(isoDate);
  if (!d) return "";
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/**
 * Is the checkin less than 60 days from `now`? (D9 short-lead; session 47 —
 * was 8 weeks, now second_installment_days=60.)
 * @param {string} checkinIso
 * @param {number} [now] - epoch ms (default Date.now(); overridable for tests)
 * @returns {boolean}
 */
export const SECOND_INSTALLMENT_DAYS = 60;
export function isShortLead(checkinIso, now = Date.now()) {
  const checkin = parseISO(checkinIso);
  if (!checkin) return false;
  return checkin.getTime() - now < SECOND_INSTALLMENT_DAYS * DAY_MS;
}

/**
 * Build an array of blank per-night rate rows for a date range.
 * @param {string} checkin - ISO date
 * @param {string} checkout - ISO date (exclusive)
 * @returns {Array<{date:string, rate:string}>}
 */
export function initBlankRates(checkin, checkout) {
  const start = parseISO(checkin);
  const end = parseISO(checkout);
  if (!start || !end) return [];
  const nights = Math.round((end.getTime() - start.getTime()) / DAY_MS);
  if (nights <= 0) return [];
  const out = [];
  for (let i = 0; i < nights; i++) {
    out.push({ date: toISO(new Date(start.getTime() + i * DAY_MS)), rate: "" });
  }
  return out;
}

/**
 * Compute the second installment due date = checkin − N days, formatted as
 * "April 29th, 2026".
 * @param {string} checkinIso
 * @param {number} [days=SECOND_INSTALLMENT_DAYS]
 * @returns {string}
 */
export function computeSecondDueDate(checkinIso, days = SECOND_INSTALLMENT_DAYS) {
  const checkin = parseISO(checkinIso);
  if (!checkin) return "";
  const due = new Date(checkin.getTime() - days * DAY_MS);
  return formatOrdinalDateYear(toISO(due));
}

/**
 * Exact Square online-lane passthrough for ONE payment (session 47): the
 * charge amount such that after Square takes pct% of the gross + fixedCents
 * the net is `amount` — rounded UP to the cent so the net is never short.
 * Mirrors CF quote_math.py gross_up_leg / brain Quote.gs Quote_grossUpLeg.
 */
export function grossUpLeg(amount, pct = 3.3, fixedCents = 30) {
  if (!(amount > 0)) return 0;
  return Math.ceil(((amount + fixedCents / 100) / (1 - pct / 100)) * 100) / 100;
}

// ── Self-booking override (session 46) ───────────────────────────────────
// A booking whose guest email is the HOUSE email is the owners walking the
// flow themselves — the production smoke test of the card lane (a real $1
// charge per installment, then a $1 damage charge on the card on file). The
// quote's bottom line is forced no matter what rates/fees are entered, so the
// walkthrough never moves real money. Component lines (room, tax, ...) stay
// as computed so the preview still renders realistically. Safe by
// construction: no real guest can hold the house email. Kept in lockstep with
// the CF (quote_admin/quote_math.py) and the brain (Quote.gs).
export const SELF_BOOKING_EMAIL = "info@onebeachroad.com";
export const SELF_BOOKING_TOTAL = 2; // = $1 first installment + $1 second

/** True when the guest email is the house email (a self-booking). */
export function isSelfBooking(email) {
  return String(email || "").trim().toLowerCase() === SELF_BOOKING_EMAIL;
}

/**
 * Force a self-booking's bottom line: $2 total, $1 + $1 installments, card
 * fee waived (the $2 stays exactly $2), short-lead cleared so the split
 * always renders. Returns the input unchanged for ordinary guests.
 */
export function applySelfBookingOverride(q, email) {
  if (!isSelfBooking(email)) return q;
  q.cardFee = 0;
  q.fullAmount = SELF_BOOKING_TOTAL;
  q.firstRate = SELF_BOOKING_TOTAL / 2;
  q.secondRate = SELF_BOOKING_TOTAL / 2;
  q.shortLead = false;
  q.selfBooking = true;
  return q;
}

/**
 * Compute all derived quote figures from raw form inputs. Pure; used by both
 * the panel (for live display) and buildPreviewText (single source of truth).
 * @param {Object} opts - rates, cleaningFee, wedding, discountPct, checkinIso,
 *   channel, now; session 47: paymentChannel + cardFeeWaived + cardFeePct
 *   (default 3.3) + cardFeeFixedCents (default 30) drive the EXACT-cost card
 *   passthrough — each installment is grossed up by grossUpLeg so Square's
 *   3.3% + 30¢ per payment is recovered to the cent (supersedes the flat 3%
 *   fee). The fee base is the pre-fee payable total; the fee itself is never
 *   taxed. The fee is always a VISIBLE line — the session-47 "hide the fee in
 *   the room rate" option (pre-inflated rates + cardFeeWaived) was removed.
 *   Session 46: optional `email` — the house email forces the self-booking $2
 *   bottom line.
 * @returns {Object} { roomSubtotal, weddingFee, tax, discountAmount, cardFee,
 *   fullAmount, shortLead, firstRate, secondRate, vrboSavings }
 */
export function computeQuote({ rates, cleaningFee, wedding, discountPct, checkinIso, channel, now, paymentChannel, cardFeeWaived, cardFeePct, cardFeeFixedCents, email }) {
  const roomSubtotal = rates.reduce((s, r) => s + (Number(r.rate) || 0), 0);
  const weddingFee = wedding === "yes" ? 1500 : 0;
  const discountAmount = discountPct > 0 ? Math.round((roomSubtotal * discountPct) / 100 * 100) / 100 : 0;
  const discountedRoom = Math.max(0, roomSubtotal - discountAmount);
  // Tax on the DISCOUNTED room (lockstep with the CF — was on the full
  // subtotal here before session 47, a preview-only divergence).
  const tax = Math.round(discountedRoom * 0.125 * 100) / 100;
  const base = Math.round((discountedRoom + cleaningFee + weddingFee + tax) * 100) / 100;
  const shortLead = isShortLead(checkinIso, now);
  const firstBase = shortLead ? base : Math.round(base * 0.5 * 100) / 100;
  const secondBase = shortLead ? 0 : Math.round((base - firstBase) * 100) / 100;
  const pct = cardFeePct == null ? 3.3 : Number(cardFeePct);
  const fixedCents = cardFeeFixedCents == null ? 30 : Number(cardFeeFixedCents);
  const card = paymentChannel === "card" && !cardFeeWaived;
  const firstRate = card ? grossUpLeg(firstBase, pct, fixedCents) : firstBase;
  const secondRate = card ? grossUpLeg(secondBase, pct, fixedCents) : secondBase;
  const fullAmount = Math.round((firstRate + secondRate) * 100) / 100;
  const cardFee = card ? Math.round((fullAmount - base) * 100) / 100 : 0;
  const vrboSavings = channel === "direct" ? Math.round(roomSubtotal * 0.05 * 100) / 100 : 0;
  return applySelfBookingOverride(
    { roomSubtotal, weddingFee, tax, discountAmount, cardFee, fullAmount, shortLead, firstRate, secondRate, vrboSavings },
    email,
  );
}

// Are all per-night rates present and equal (compact room-line form)?
function isUniform(rates) {
  if (!rates.length) return false;
  const first = Number(rates[0].rate);
  if (!first) return false;
  return rates.every((r) => Number(r.rate) === first);
}

/**
 * Build the exact guest email preview text from the live form state. Matches
 * the quote_guest template (§3.1 / §3.2). Julie sees what the guest will see.
 * @param {Object} ctx - { booking, rates, cleaningFee, wedding, discountSubject,
 *   discountPct, paymentChannel, now }
 * @returns {string}
 */
export function buildPreviewText({ booking, rates, cleaningFee, wedding, discountSubject, discountPct, paymentChannel, cardFeeWaived, now }) {
  const q = computeQuote({
    rates,
    cleaningFee,
    wedding,
    discountPct,
    checkinIso: booking?.checkin_date,
    channel: booking?.channel,
    paymentChannel,
    cardFeeWaived,
    email: booking?.email,
    now,
  });
  const firstName = booking?.first_name || "there";
  const checkinIso = booking?.checkin_date;
  const checkoutIso = booking?.checkout_date;
  const nights = rates.length;
  const lines = [];

  // Greeting
  const checkinFriendly = formatOrdinalDate(checkinIso);
  const checkoutFriendly = formatOrdinalDate(checkoutIso);
  // Null-guarded: a non-ISO date string (e.g. a sheet serial rendered M/D/YYYY)
  // parses to null — degrade to a year-less greeting, never throw in render.
  const checkoutYear = parseISO(checkoutIso)?.getUTCFullYear() ?? "";
  lines.push(`Hi ${firstName} - I have a quote for you for the dates of ${checkinFriendly} - ${checkoutFriendly}, ${checkoutYear}.`);
  lines.push("");

  // Room section
  if (isUniform(rates)) {
    lines.push(`${formatMoney(Number(rates[0].rate))} x ${nights} nights = ${formatMoney(q.roomSubtotal)}`);
  } else {
    for (const r of rates) {
      lines.push(`${formatMonthDay(r.date)}: ${formatMoney(Number(r.rate))}`);
    }
    lines.push(`Room subtotal = ${formatMoney(q.roomSubtotal)}`);
  }

  // Cleaning
  lines.push(`Cleaning = ${formatMoney(cleaningFee)}`);

  // Wedding
  if (wedding === "yes") {
    lines.push(`Wedding fee = ${formatMoney(q.weddingFee)}`);
  }

  // Discount
  if (discountPct > 0 && discountSubject) {
    lines.push(`${discountSubject} -${discountPct}% = -${formatMoney(q.discountAmount)}`);
  }

  // Tax
  lines.push(`Hotel tax = ${formatMoney(q.tax)}`);

  // Card processing fee (only when a fee applies as a visible line — never
  // for check/wire, a host-waived card booking, or a hidden-in-rate quote)
  if (q.cardFee > 0) {
    lines.push(`Card processing fee (3.3% + 30¢ per payment) = ${formatMoney(q.cardFee)}`);
  }
  lines.push("");

  // Total
  lines.push(`Total = ${formatMoney(q.fullAmount)}`);
  // Session 49 (user, explicit + regression-pinned): the deposit is a LINE
  // ITEM in every quote, always — PAID with the 1st installment and returned.
  // Session 54: the card deposit is a REAL refundable deposit again (own
  // payment link, own card-fee passthrough) — the "pre-authentication (held,
  // not charged)" wording is gone: no hold ever existed.
  if (paymentChannel === "card") {
    lines.push(`+ ${formatMoney(5000)} refundable security deposit (paid by card with the first payment, returned after your stay)`);
  } else {
    lines.push(`+ ${formatMoney(5000)} security deposit (paid with the first payment, returned after your stay)`);
  }
  lines.push("");

  // Reserve block. Session 49 (user): the card lane NEVER shows "$0".
  // Session 54: card is the SAME refundable deposit as check/wire, just by
  // card (with its own exact card-fee passthrough on the deposit link) — the
  // expectation is set identically for every payment method. The deposit
  // line stays arithmetic-true: the deposit is really collected + returned.
  lines.push("To reserve dates:");
  const isCard = paymentChannel === "card";
  const deposit = 5000;
  if (isCard) {
    lines.push(`${formatMoney(q.firstRate)} + ${formatMoney(deposit)} refundable security deposit (also by card, returned after your stay)`);
  } else {
    lines.push(`${formatMoney(q.firstRate)} + ${formatMoney(deposit)} security deposit = ${formatMoney(q.firstRate + deposit)}`);
  }
  lines.push("");

  // Installment due line (all payment methods): the reserve-total line above
  // already carries the first installment — restating it here read as the
  // same rate printed twice back-to-back. Only the balance is named.
  if (q.shortLead) {
    lines.push(`Full payment of ${formatMoney(q.fullAmount)} before booking.`);
  } else {
    lines.push(`${formatMoney(q.secondRate)} ${SECOND_INSTALLMENT_DAYS} days before arrival`);
  }

  return lines.join("\n");
}
